import { createFileRoute, Link } from '@tanstack/react-router';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { useProperty } from '@/lib/property/propertyContextDef';
import { useBookingsInRange, useOutstandingBalances, guestName, listingTitle } from '@/lib/data/bookings';
import { useBookingsRealtime } from '@/lib/data/bookingsRealtime';
import { computeBookingMoney } from '@/lib/money/bookingMoney';
import { formatNad } from '@/lib/money/format';
import { StatusPill } from '@/lib/bookings/StatusPill';
import { CheckmarkIcon, CalendarIcon, PrintIcon } from '@/icons';
import { CashIcon, ListIcon, PeopleIcon, WalletIcon } from '@/icons/nav';
import { SkeletonTiles } from '@/ui/Loader';
import { ButtonLink, Card, PageHeader, SectionTitle, StatTile } from '@/ui/primitives';

export const Route = createFileRoute('/')({
  component: Dashboard,
});

/** Dashboard (task-13 brief scope: "today's arrivals, outstanding
 * balances"). "This month" (arrivals count, bookings count, fees) comes
 * from `useBookingsInRange`, the same range-fetch the calendar and
 * arrivals-print routes already use. "Outstanding balances" is
 * deliberately a SEPARATE, unscoped query (`useOutstandingBalances`,
 * review round 1 item 3) — a booking that checked in last month and still
 * hasn't paid must not disappear from this tile just because its check-in
 * date fell outside the current month window. Review status / KYC-
 * agreement status (spec §5.2) aren't built here: neither has a data
 * source wired up by this task, and adding a placeholder pill for data
 * nothing produces yet would be speculative — a later task (verification/
 * agreement screens) is the natural place to add it. */
function Dashboard() {
  const { businessId } = useProperty();
  useBookingsRealtime(businessId);
  const today = format(new Date(), 'yyyy-MM-dd');
  const monthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');
  const { data: bookings, isLoading } = useBookingsInRange(businessId, monthStart, monthEnd);
  const { data: outstandingData, isLoading: outstandingLoading } = useOutstandingBalances(businessId);

  const monthBookings = bookings ?? [];
  const todaysArrivals = monthBookings.filter(
    (b) => b.check_in === today && b.status !== 'cancelled' && b.status !== 'rejected',
  );
  // `useOutstandingBalances` already filters to non-terminal, non-no-show
  // rows with an owing balance_status server-side; re-derive the money leg
  // here only to sort/sum/display it, not to re-decide membership.
  const outstanding = outstandingData ?? [];
  const balanceDueTodayCents = outstanding
    .filter((b) => b.check_in === today)
    .reduce((sum, b) => {
      const money = computeBookingMoney({
        totalPrice: b.total_price,
        depositAmount: b.deposit_amount,
        balanceRecordedAmountNad: b.balance_recorded_amount_nad,
        feeStatus: b.fee_status,
        balanceStatus: b.balance_status,
      });
      return sum + money.balanceDueCents;
    }, 0);
  // A full in-app charge (no deposit snapshot) is the guest's whole payment,
  // not NamibiaGo's fee, so it stays out of the fees tile (final fix wave C1).
  const feesThisMonthCents = monthBookings.reduce((sum, b) => {
    if (b.deposit_amount === null || b.deposit_amount === undefined) return sum;
    const money = computeBookingMoney({
      totalPrice: b.total_price,
      depositAmount: b.deposit_amount,
      balanceRecordedAmountNad: b.balance_recorded_amount_nad,
      feeStatus: b.fee_status,
      balanceStatus: b.balance_status,
    });
    return sum + money.feePaidCents;
  }, 0);

  const loading = isLoading || outstandingLoading;
  const dateLabel = format(new Date(), 'EEEE d MMMM');

  return (
    <section className="flex flex-col gap-xl">
      <PageHeader
        title="Dashboard"
        eyebrow={dateLabel}
        actions={
          <>
            <ButtonLink to="/bookings/calendar" variant="secondary" icon={<CalendarIcon className="h-4 w-4" aria-hidden="true" />}>
              Calendar
            </ButtonLink>
            <ButtonLink to="/check-in" variant="primary" icon={<CheckmarkIcon className="h-4 w-4" aria-hidden="true" />}>
              Check in
            </ButtonLink>
          </>
        }
      />

      {loading ? (
        <SkeletonTiles />
      ) : (
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-4">
          <StatTile label="Today's arrivals" value={todaysArrivals.length} icon={<PeopleIcon />} />
          <StatTile
            label="Balance due today"
            value={formatNad(balanceDueTodayCents / 100)}
            tone={balanceDueTodayCents > 0 ? 'attention' : 'default'}
            icon={<WalletIcon />}
          />
          <StatTile label="Bookings this month" value={monthBookings.length} icon={<ListIcon />} />
          <StatTile label="Fees guests paid this month" value={formatNad(feesThisMonthCents / 100)} icon={<CashIcon />} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-lg lg:grid-cols-2">
        <Card>
          <SectionTitle
            aside={
              <Link
                to="/bookings/arrivals-print"
                target="_blank"
                className="inline-flex items-center gap-xs text-small font-semibold text-canyon hover:underline"
              >
                <PrintIcon className="h-4 w-4" aria-hidden="true" />
                Print list
              </Link>
            }
          >
            Today's arrivals
          </SectionTitle>
          {todaysArrivals.length === 0 ? (
            <p className="mt-md rounded-tile bg-surface-2 px-md py-sm text-body text-ink-2">No arrivals today.</p>
          ) : (
            <ul className="mt-md divide-y divide-hair">
              {todaysArrivals.map((b) => (
                <li key={b.id} className="flex items-center justify-between gap-sm py-sm">
                  <Link
                    to="/bookings/$reference"
                    params={{ reference: b.reference_number ?? b.id }}
                    className="min-w-0 truncate text-body font-semibold text-ink hover:text-canyon"
                  >
                    {guestName(b)} <span className="font-normal text-ink-2">· {listingTitle(b)}</span>
                  </Link>
                  <StatusPill status={b.display_status} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <SectionTitle>Outstanding balances</SectionTitle>
          {outstanding.length === 0 ? (
            <p className="mt-md rounded-tile bg-surface-2 px-md py-sm text-body text-ink-2">Nothing outstanding.</p>
          ) : (
            <ul className="mt-md divide-y divide-hair">
              {outstanding.slice(0, 8).map((b) => {
                const money = computeBookingMoney({
                  totalPrice: b.total_price,
                  depositAmount: b.deposit_amount,
                  balanceRecordedAmountNad: b.balance_recorded_amount_nad,
                  feeStatus: b.fee_status,
                  balanceStatus: b.balance_status,
                });
                return (
                  <li key={b.id} className="flex items-center justify-between gap-sm py-sm">
                    <Link
                      to="/bookings/$reference"
                      params={{ reference: b.reference_number ?? b.id }}
                      className="min-w-0 truncate text-body font-semibold text-ink hover:text-canyon"
                    >
                      {guestName(b)}
                      {b.check_in ? <span className="font-normal text-ink-2"> · arrives {b.check_in}</span> : null}
                    </Link>
                    <span className="text-body font-bold tabular-nums text-canyon">{formatNad(money.balanceDueCents / 100)}</span>
                  </li>
                );
              })}
              {outstanding.length > 8 ? (
                <li className="pt-sm text-caption text-ink-3">
                  +{outstanding.length - 8} more —{' '}
                  <Link to="/bookings" className="font-semibold text-canyon hover:underline">
                    see the bookings list
                  </Link>
                </li>
              ) : null}
            </ul>
          )}
        </Card>
      </div>
    </section>
  );
}
